import { ReactNode, useEffect, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { BaselineIcon, ComparisonIcon, DeltaIcon, UpIcon } from "../../../../../../assets/icons";
import { formatCompactNumber } from "../../../../../../common/utils";
import HorizontalBarChart from "../../../../../../components/charts/graph/HorizontalBarChart";
import PieChartDistributionType from "../../../../../../components/charts/graph/PieChartDistributionType";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import {
	MeasurableValueDTO,
	RootCauseListPayload,
	RootCauseWidgetAggregateDTO,
	RootCauseWidgetAggregateTkmItemDTO,
} from "../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../../hooks/useResource";
import { AggregateCard } from "./AggregateCard";
import { getTransportTypeIcon, mapTkmSplitToColors, mapTkmSplitToDataChart } from "./data";

export type AggregateMetricKey = "co2" | "tkm" | "intensity" | "avgDistance";

export type AggregateCardData = Record<AggregateMetricKey, MeasurableValueDTO | undefined>;

export type AggregateCardConfig = {
	type: "baseline" | "comparison" | "delta";
	icon: ReactNode;
	data: AggregateCardData;
	isDelta?: boolean;
	chart: ReactNode;
	isLoading?: boolean;
};

type AggregateScenarioProps = {
	onWarningsChange?: (warnings?: string[]) => void;
};

export const AggregateScenario = ({ onWarningsChange }: AggregateScenarioProps) => {
	const { rootCauseState } = useFiltersRegistry();

	const { data, isLoading } = useReadResource<RootCauseListPayload, RootCauseWidgetAggregateDTO>(
		{
			route: ApiRoutes.RootCauseWidjetAggregate,
		},
		{
			readMethod: "post",
		},
		{ ...rootCauseState.payload }
	);

	useEffect(() => {
		onWarningsChange?.(data?.warnings ?? []);
	}, [data?.warnings]);

	const aggregateData = useMemo<AggregateCardConfig[]>(() => {
		const baseTkmSplit = data?.baseTkmSplit ?? [];
		const compTkmSplit = data?.compTkmSplit ?? [];
		const deltaChartData = (data?.tkmSplitDelta ?? []).map((item: RootCauseWidgetAggregateTkmItemDTO) => ({
			id: item.haulageId,
			value: item.tkm.value,
			icon: getTransportTypeIcon(item.haulageId),
			tooltip: (
				<Stack gap={0.5}>
					<Typography variant="subtitle2">{item.haulageLabel}</Typography>
					<Stack spacing={1} direction={"row"} alignItems={"end"}>
						<Typography variant="body2">{formatCompactNumber(item.tkm.value)}</Typography>
						<Typography variant="caption" color="inherit">
							{item.tkm.measure}
						</Typography>
					</Stack>
				</Stack>
			),
		}));

		return [
			{
				type: "baseline",
				icon: <BaselineIcon color="primary" />,
				data: {
					co2: data?.baseCo2,
					tkm: data?.baseTkm,
					intensity: data?.baseIntensity,
					avgDistance: data?.baseAvgDistance,
				},
				chart: (
					<PieChartDistributionType
						isLoading={isLoading}
						totalEmissions={data?.baseTkm?.value}
						keyEmissions={data?.baseTkm?.measure}
						dataChart={mapTkmSplitToDataChart(baseTkmSplit)}
						colors={mapTkmSplitToColors(baseTkmSplit)}
						elevation={0}
					/>
				),
			},
			{
				type: "comparison",
				icon: <ComparisonIcon color="primary" />,
				data: {
					co2: data?.compCo2,
					tkm: data?.compTkm,
					intensity: data?.compIntensity,
					avgDistance: data?.compAvgDistance,
				},
				chart: (
					<PieChartDistributionType
						isLoading={isLoading}
						totalEmissions={data?.compTkm?.value}
						keyEmissions={data?.compTkm?.measure}
						dataChart={mapTkmSplitToDataChart(compTkmSplit)}
						colors={mapTkmSplitToColors(compTkmSplit)}
						elevation={0}
					/>
				),
			},
			{
				type: "delta",
				icon: <DeltaIcon color="primary" />,
				data: {
					co2: data?.co2Delta,
					tkm: data?.tkmDelta,
					intensity: data?.intensityDelta,
					avgDistance: data?.avgDistanceDelta,
				},
				isDelta: true,
				chart: <HorizontalBarChart data={deltaChartData} showLegend={false} />,
			},
		];
	}, [data, isLoading]);

	return (
		<Stack spacing={2} direction="row">
			{aggregateData.map((aggregate) => (
				<AggregateCard
					key={aggregate.type}
					type={aggregate.type}
					icon={aggregate.icon}
					data={aggregate.data}
					isDelta={aggregate.isDelta}
					isLoading={isLoading}
					chart={aggregate.chart}
				/>
			))}
		</Stack>
	);
};
